import { create } from 'zustand';
import { persist } from 'zustand/middleware';

interface AudioDevicesStore {
  micId: string | null;     // preferred microphone deviceId
  speakerId: string | null; // preferred speaker (output) deviceId
  /**
   * Suppress background noise (other people talking nearby, room hum) on the
   * outbound mic. ON by default. When on, the softphone runs RNNoise over the
   * mic before sending it; when off, only the browser's built-in DSP applies.
   */
  noiseSuppression: boolean;
  /**
   * Quality tuning — browser DSP on the outbound mic. Both ON by default
   * (the safe choice); turning them off can improve fidelity in specific
   * setups: echoCancellation OFF with a good headset avoids the "processed"
   * sound; autoGainControl OFF stops the volume "breathing" up and down.
   */
  echoCancellation: boolean;
  autoGainControl: boolean;
  /** Softphone output volume, 0-100. Applied to the remote <audio> element. */
  speakerVolume: number;
  /**
   * Microphone input gain, percent (100 = as captured). A WebAudio GainNode is
   * inserted into the outbound pipeline when ≠100 — the fix for a mic that is
   * simply too quiet (boost above 100%) or too hot (below 100%).
   */
  micGain: number;
  /**
   * Noise gate: pass the mic only while it's louder than gateThreshold. The
   * fix for "the customer hears my neighbors" — the agent (close to the mic)
   * opens the gate, quieter nearby voices don't. OFF by default.
   */
  gateEnabled: boolean;
  gateThreshold: number; // 0-100 (UI scale; see noiseGate.ts mapping)
  setMic: (id: string | null) => void;
  setSpeaker: (id: string | null) => void;
  setNoiseSuppression: (v: boolean) => void;
  setEchoCancellation: (v: boolean) => void;
  setAutoGainControl: (v: boolean) => void;
  setSpeakerVolume: (v: number) => void;
  setMicGain: (v: number) => void;
  setGateEnabled: (v: boolean) => void;
  setGateThreshold: (v: number) => void;
}

/**
 * The agent's chosen mic + speaker and audio preferences. Persisted so they
 * survive reloads. The softphone reads micId for getUserMedia constraints,
 * speakerId is applied to the remote <audio> element via setSinkId, and
 * noiseSuppression toggles the RNNoise mic filter.
 */
export const useAudioDevices = create<AudioDevicesStore>()(
  persist(
    (set) => ({
      micId: null,
      speakerId: null,
      noiseSuppression: true,
      echoCancellation: true,
      autoGainControl: true,
      speakerVolume: 100,
      micGain: 100,
      gateEnabled: false,
      gateThreshold: 35,
      setMic: (id) => set({ micId: id }),
      setSpeaker: (id) => {
        set({ speakerId: id });
        applySpeaker(id);
      },
      setNoiseSuppression: (v) => set({ noiseSuppression: v }),
      setEchoCancellation: (v) => set({ echoCancellation: v }),
      setAutoGainControl: (v) => set({ autoGainControl: v }),
      setSpeakerVolume: (v) => {
        const vol = Math.max(0, Math.min(100, Math.round(v)));
        set({ speakerVolume: vol });
        applySpeakerVolume(vol);
      },
      // 25%-400%: enough headroom to rescue a genuinely quiet mic without
      // letting a stray value blast the far end.
      setMicGain: (v) => set({ micGain: Math.max(25, Math.min(400, Math.round(v))) }),
      setGateEnabled: (v) => set({ gateEnabled: v }),
      setGateThreshold: (v) => set({ gateThreshold: Math.max(0, Math.min(100, Math.round(v))) }),
    }),
    { name: 'merkaziya-audio-devices' }
  )
);

/** Apply a speaker deviceId to the softphone's remote audio element. */
export function applySpeaker(id: string | null): void {
  if (typeof document === 'undefined' || !id) return;
  const el = document.getElementById('merkaziya-remote-audio') as
    (HTMLAudioElement & { setSinkId?: (id: string) => Promise<void> }) | null;
  if (el && typeof el.setSinkId === 'function') {
    el.setSinkId(id).catch(() => { /* unsupported / not yet permitted */ });
  }
}

/** Apply the chosen output volume (0-100) to the softphone's remote audio. */
export function applySpeakerVolume(vol: number): void {
  if (typeof document === 'undefined') return;
  const el = document.getElementById('merkaziya-remote-audio') as HTMLAudioElement | null;
  if (el) el.volume = Math.max(0, Math.min(1, vol / 100));
}

/**
 * Microphone constraint for getUserMedia, honoring the chosen device and the
 * user's quality tuning (echoCancellation / autoGainControl toggles). The
 * browser's built-in `noiseSuppression` is controlled by
 * `builtinNoiseSuppression`: keep it ON when RNNoise is NOT running, and turn
 * it OFF when RNNoise will run (stacking two denoisers double-processes the
 * signal and muddies the agent's voice).
 */
export function micConstraint(builtinNoiseSuppression = true): MediaTrackConstraints {
  const { micId, echoCancellation, autoGainControl } = useAudioDevices.getState();
  const c: MediaTrackConstraints = {
    echoCancellation: { ideal: echoCancellation },
    autoGainControl: { ideal: autoGainControl },
    noiseSuppression: { ideal: builtinNoiseSuppression },
  };
  if (micId) c.deviceId = { exact: micId };
  return c;
}
